/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    InHomeLocationApi,
    InHomeLocationGetRequest,
} from 'client-code/in-home-location';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { IInHomeLocationServiceRepository } from '../interfaces/IInHomeLocationServiceRepository';
import { Log } from '../logging/Log';
import { Storage } from '../storage/Storage';
import { TInHomeLocation } from '../types/TInHomeLocation';
import { TServicesParams } from '../types/TServicesParams';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class InHomeLocationServiceRepositoryImpl implements IInHomeLocationServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'InHomeLocationServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getInHomeLocationFromService(): Promise<TInHomeLocation> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;
            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: InHomeLocationGetRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: DeviceModel.getAppVersionName(),
                bodyId: this.mParams.bodyId,
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const inHomeLocationService = new InHomeLocationApi(configuration);
            const inHomeLocationServiceResponse = await inHomeLocationService.inHomeLocationGet(
                requestParameters,
            );
            const inHomeLocation = {
                status: Constants.IN_HOME_LOCATION_STATUS.SUCCESS,
                response: inHomeLocationServiceResponse.data,
            };

            Storage.getInstance().setItem(Storage.Keys.IN_HOME_LOCATION_DATA, inHomeLocation);

            return inHomeLocation;
        } catch (error: any) {
            Log.e(this.TAG, 'getInHomeLocationFromService', error);
            if (!error.response) {
                const inHomeLocation: TInHomeLocation = {
                    status: Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE,
                };
                Storage.getInstance().setItem(Storage.Keys.IN_HOME_LOCATION_DATA, inHomeLocation);
            } else {
                const inHomeLocation: TInHomeLocation = {
                    status: Constants.IN_HOME_LOCATION_STATUS.ERROR,
                    response: error.response,
                };
                Storage.getInstance().setItem(Storage.Keys.IN_HOME_LOCATION_DATA, inHomeLocation);
            }
            throw error;
        }
    }

    /**
     * function to fetch InHomeLocation and update local storage
     * @returns { TInHomeLocation }
     */
    public async fetchAndStoreInHomeLocation(): Promise<TInHomeLocation> {
        try {
            return await this.getInHomeLocationFromService();
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreInHomeLocation', error);
            throw error;
        }
    }

    /**
     * returns InHomeLocation from local storage
     * @returns { TInHomeLocation }
     */
    public getInHomeLocation(): TInHomeLocation {
        try {
            return Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
        } catch (error) {
            Log.e(this.TAG, 'fetchInHomeLocation', error);
            throw error;
        }
    }
}
